import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  ViewEncapsulation,
} from '@angular/core';

import { GettingStartedDrawerComponent } from '@core/getting-started-legacy';
import { TellentGettingStartedFacade } from '@core/tellent-getting-started-data';
import { TellentGettingStartedSubtaskViewModel } from '@core/tellent-getting-started-types';
import { TellentNavigationRoutingService } from '@core/tellent-logic';
import { TellentAdoptedStylesheetsDirective, TellentThemeDirective } from '@core/tellent-smart-ui';
import { TellentWebComponent } from '@core/tellent-types';
import { SpinnerComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'x-getting-started-drawer',
  template: `@let isDrawerReady = svc.isDrawerReady();
    @let drawer = svc.drawer()!;
    @let steps = svc.steps()!;
    @let progressPct = svc.progressPct();
    @let showDismiss = svc.isDrawerTriggerVisible();

    @if (isDrawerReady) {
      <rt-getting-started-drawer
        [progress]="progressPct"
        [copy]="drawer"
        [steps]="steps"
        [dismissGuideButtonVisible]="showDismiss!"
        (close)="close.emit()"
        (dismissGuide)="svc.dismissGuide(); dismiss.emit()"
        (subtaskCta)="subtaskCtaHandler($event)"
        (subtaskMarkAsDone)="subtaskMarkAsDoneHandler($event)"
      />
    } @else {
      <rt-spinner />
    }`,
  imports: [GettingStartedDrawerComponent, SpinnerComponent],
  styleUrls: ['../../../styles.less'],
  styles: `
    :host {
      display: contents;
    }
  `,
  hostDirectives: [TellentThemeDirective, TellentAdoptedStylesheetsDirective],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TellentGettingStartedDrawerWrapComponent {
  public readonly svc = inject(TellentGettingStartedFacade);
  public readonly navSvc = inject(TellentNavigationRoutingService);

  public readonly close = output<void>();
  public readonly dismiss = output<void>();

  public readonly subtaskCta = output<{
    stepKey: string;
    subtask: TellentGettingStartedSubtaskViewModel;
  }>();

  public readonly subtaskMarkAsDone = output<{
    stepKey: string;
    subtask: TellentGettingStartedSubtaskViewModel;
  }>();

  public subtaskCtaHandler({ stepKey, subtaskKey }: { stepKey: string; subtaskKey: string }) {
    const subtask = this.svc.findSubtask(stepKey, subtaskKey);
    if (!subtask) return;

    switch (subtask.metadata.action?.kind) {
      case 'external_url':
        window.open(subtask.metadata.action.target, '_blank');
        break;

      case 'internal_url':
        this.navSvc.navigateById({
          id: subtask.metadata.action.target,
          appKind: subtask.metadata.action.appKind,
        });
        break;
      default:
        break;
    }

    this.subtaskCta.emit({ stepKey, subtask });
  }

  public subtaskMarkAsDoneHandler({
    stepKey,
    subtaskKey,
  }: {
    stepKey: string;
    subtaskKey: string;
  }) {
    const subtask = this.svc.findSubtask(stepKey, subtaskKey);
    if (!subtask) return;

    this.svc.updateStepsProgress([subtaskKey]);
    this.subtaskMarkAsDone.emit({ stepKey, subtask });
  }
}

export type TellentGettingStartedDrawerWebComponent = TellentWebComponent<
  TellentGettingStartedDrawerWrapComponent,
  'close' | 'dismiss' | 'subtaskCta' | 'subtaskMarkAsDone'
>;
